import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { X, Download, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';

export const FullscreenGallery = ({ images, initialIndex, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isDownloading, setIsDownloading] = useState(false);

  const goToNext = useCallback(() => setCurrentIndex((prev) => (prev + 1) % images.length), [images.length]);
  const goToPrev = useCallback(() => setCurrentIndex((prev) => (prev - 1 + images.length) % images.length), [images.length]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goToPrev();
      if (e.key === 'ArrowRight') goToNext();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = 'auto';
    };
  }, [onClose, goToNext, goToPrev]);

  const handleDownload = async () => {
    const img = images[currentIndex];
    if (!img?.url) return;
    setIsDownloading(true);
    try {
      const response = await fetch(img.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `tryon-${Date.now()}.jpg`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      window.open(img.url, '_blank');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black flex flex-col"
      style={{ height: '100dvh' }}
    >
      <div className="flex-shrink-0 flex items-center justify-between p-3 sm:p-4 text-white">
        <span className="text-sm text-white/70">{currentIndex + 1} / {images.length}</span>
        <div className="flex gap-2">
          <button onClick={handleDownload} disabled={isDownloading} className="p-2 hover:bg-white/10 rounded-full">
            {isDownloading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
          </button>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 flex items-center justify-center p-2 sm:p-4 relative overflow-hidden">
        {images.length > 1 && (
          <>
            <button onClick={goToPrev} className="absolute left-1 sm:left-2 p-2 bg-white/10 hover:bg-white/20 rounded-full z-10">
              <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </button>
            <button onClick={goToNext} className="absolute right-1 sm:right-2 p-2 bg-white/10 hover:bg-white/20 rounded-full z-10">
              <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </button>
          </>
        )}
        <motion.img
          key={currentIndex}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          src={images[currentIndex]?.url}
          alt=""
          className="max-w-full max-h-full object-contain"
          style={{ maxHeight: 'calc(100dvh - 140px)' }}
        />
      </div>

      {images.length > 1 && (
        <div className="flex-shrink-0 p-2 sm:p-4 flex justify-center gap-2 overflow-x-auto">
          {images.map((img, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`w-12 h-12 sm:w-14 sm:h-14 rounded-lg overflow-hidden border-2 transition-all flex-shrink-0 ${
                currentIndex === idx ? 'border-white scale-105' : 'border-transparent opacity-60'
              }`}
            >
              <img src={img.url} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </motion.div>
  );
};
